<template>
    <cod-dialog
        ref="dialog"
        :show.sync="showDialog"
        :drawer="drawer"
        max-width="800px"
    >
        <!-- Header -->
        <div slot="header">
            <div>{{ $t('beginning_inventories.add') }}</div>
        </div>

        <!-- Content -->
        <div slot="content" class="item-form">
            <v-row>
                <v-col cols="8">
                    <div class="d-flex">
                        <search-product
                            ref="searchProduct"
                            class="flex-1"
                            :label="$t('general.inventory_item')"
                            :products="products"
                            :disable-quantity="true"
                            :error-messages="form.$errors.product_id"
                            @select="selectProduct($event.product)"
                            @blur="form.validate('product_id');"
                        />

                        <v-btn
                            class="ml-3"
                            icon
                            @click="drawer = 'product'"
                        >
                            <v-icon v-if="!product">fal fa-plus</v-icon>
                            <v-icon v-else>fal fa-pen</v-icon>
                        </v-btn>
                    </div>
                </v-col>

                <v-col cols="4">
                    <number-field
                        ref="quantity"
                        v-model="form.quantity"
                        :label="$t('beginning_inventories.quantity')"
                        :precision="3"
                        dense
                        unsigned
                        use-string
                        :disabled="!form.product_id"
                        :error-messages="form.$errors.quantity"
                        @blur="form.validate('quantity')"
                        @input="onInput('quantity')"
                    />
                </v-col>
            </v-row>

            <v-row class="highlight-section">
                <v-col cols="12" class="pb-1 primary--text">{{ $t('beginning_inventories.section_item_price') }}</v-col>

                <v-col cols="6" sm="3">
                    <number-field
                        v-model="form.selling_price"
                        :label="$t('beginning_inventories.selling_price')"
                        :precision="2"
                        dense
                        unsigned
                        use-string
                        :disabled="!form.product_id"
                        :error-messages="form.$errors.selling_price"
                        @blur="form.validate('selling_price');"
                        @input="onInput('selling_price')"
                    />
                </v-col>

                <v-col cols="6" sm="3">
                    <number-field
                        v-model="form.selling_price_after_tax"
                        :label="$t('beginning_inventories.selling_price_after_tax') + ` (${itemInternal.getTaxRate()}%)`"
                        :precision="2"
                        dense
                        unsigned
                        use-string
                        :disabled="!form.product_id"
                        :error-messages="form.$errors.selling_price_after_tax"
                        @blur="form.validate('selling_price_after_tax');"
                        @input="onInput('selling_price_after_tax')"
                    />
                </v-col>

                <v-col cols="6" sm="3">
                    <v-text-field
                        :value="itemInternal.getSellingValue().toFormat('0.00')"
                        :label="$t('beginning_inventories.selling_value')"
                        class="readonly highlight"
                        dense
                        readonly
                        disabled
                    />
                </v-col>

                <v-col cols="6" sm="3">
                    <v-text-field
                        :value="itemInternal.getSellingValueAfterTax().toFormat('0.00')"
                        :label="$t('beginning_inventories.selling_value_after_tax')"
                        class="readonly highlight"
                        dense
                        readonly
                        disabled
                    />
                </v-col>
            </v-row>

            <v-row v-if="productIsIngredient">
                <v-col>
                    <v-alert
                        icon="fal fa-exclamation-circle"
                        color="primary"
                        prominent
                        text
                    >
                        {{ $t('products.ingredients.purchase_price_alert') }}
                    </v-alert>
                </v-col>
            </v-row>
        </div>

        <!-- Footer -->
        <template slot="footer">
            <v-btn
                color="primary"
                :disabled="form.$busy"
                :loading="form.$busy"
                class="flex-1"
                @click="save"
            >
                {{ $t('general.save') }}
            </v-btn>
        </template>

        <!-- Drawer -->
        <div slot="drawer" class="drawer">
            <v-toolbar
                class="drawer-header"
                absolute
                dark
                flat
                color="primary"
            >
                <v-btn icon dark @click="drawer = null">
                    <v-icon>fal fa-times</v-icon>
                </v-btn>

                <v-toolbar-title>
                    <span v-if="!product">{{ $t('products.add_product') }}</span>
                    <span v-else-if="product.id">{{ $t('products.edit_product') }}</span>
                    <span v-else>{{ $t('products.copy_product') }}</span>
                </v-toolbar-title>
            </v-toolbar>

            <product-form
                v-if="drawer"
                class="drawer-content"
                mode="inventoryAccounting"
                :product="product"
                :products="products"
                :pricelists="pricelists"
                @save="onProductSaved"
            />
        </div>

    </cod-dialog>
</template>

<script>
import SearchProduct from '@/views/shared/components/SearchProduct';
import NumberField from '@/components/NumberField';
import BeginningInventoryItem from '@/models/BeginningInventoryItem';
import CodDialog from '@/components/CodDialog';
import ProductForm from '@/views/shared/components/ProductForm';
import HashGen from '@/utilities/HashGen';
import { required, minValue } from 'vuelidate/lib/validators';

export default {
    components: {
        SearchProduct,
        NumberField,
        CodDialog,
        ProductForm
    },

    props: {
        show: {
            type: Boolean,
            default: false
        },
        item: {
            type: Object,
            default: () => null
        },
        products: {
            type: Array,
            default: () => []
        },
        pricelists: {
            type: Array,
            default: () => []
        },
        date: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            showDialog: false,

            form: this.createForm(),
            product: null,
            drawer: null,
        };
    },

    validations() {
        return {
            form: {
                product_id: { required },
                unit_of_measure: { required },
                quantity: { required, minValue: minValue(0) },
                selling_price: { required, minValue: minValue(0.01) },
                selling_price_after_tax: { required, minValue: minValue(0.01) },
            }
        }
    },

    watch: {
        show(val) {
            this.showDialog = val;

            if (val) {
                this.drawer = null;
                this.form = this.createForm(this.item);

                setTimeout(() => {
                    const product = this.item?.product_id ? this.products.find(p => p.id === this.item.product_id) : null;
                    this.product = product;

                    if(this.$refs.searchProduct) {
                        if(product) {
                            this.$refs.searchProduct.setSelected(product);
                        } else {
                            this.$refs.searchProduct.focus();
                        }
                    }
                }, 200)
            }
        },

        showDialog(val) {
            this.$emit('update:show', val);
        },
    },

    computed: {
        itemInternal() {
            return new BeginningInventoryItem(this.form.toJson());
        },

        productIsIngredient() {
            return this.product && !this.product?.is_sold
        }
    },

    methods: {
        createForm(item) {
            return new Form(this, 'form', {
                id: item?.id || null,
                hash: item?.hash || HashGen.regular(),
                date: this.date,
                beginning_inventory_id: item?.beginning_inventory_id || null,
                product_id: item?.product_id || null,
                tax_rate: item?.tax_rate || null,
                unit_of_measure: item?.unit_of_measure || 'pcs',
                quantity: item?.quantity || '1.000',
                selling_price: item?.selling_price || '0.00',
                selling_price_after_tax: item?.selling_price_after_tax || '0.00',
            });
        },

        save() {
            if(!this.form.validate()) return;
            this.showDialog = false;
            this.$emit('save', this.itemInternal)
        },

        selectProduct(product) {
            const taxRate = product?.getTaxRate();
            if(product && !taxRate) return;

            this.product = product;

            this.form.product_id = product?.id || null;
            this.form.quantity = product?.count && product.count >= 0 ? product.count : '1.000';
            this.form.unit_of_measure = product?.unit_of_measure_sold || null;
            this.form.tax_rate = product
                ? (this.tenant.venue.taxes_enabled ? taxRate.rate : '0.00')
                : null;

            this.form.selling_price = product?.getPriceBeforeTax().toFormat('0.0000') || '0.00',
            this.form.selling_price_after_tax = product?.getPriceAfterTax().toFormat('0.0000') || '0.00',

            this.form.validate('product_id');
            this.$nextTick(() => {
                this.$refs.quantity.focus()
                this.form.reset();
            });
        },

        onInput(field) {
            delete this.form.$errors[field];

            setTimeout(() => {
                if(['selling_price', 'selling_price_after_tax'].includes(field)) {
                    const prices = this.itemInternal.calculatePrices(this.form[field], field);

                    this.form.selling_price = prices.selling_price;
                    this.form.selling_price_after_tax = prices.selling_price_after_tax;
                }
            }, 50)
        },

        /**
         * On product saved handle the updated data and close the form
         * @param data
         */
        onProductSaved(data) {
            this.drawer = null;

            const productsIndex = this.products.findIndex(item => item.id === data.product.id);
            if(productsIndex != -1) {
                this.$set(this.products, productsIndex, data.product);
            } else {
                this.products.unshift(data.product);
            }

            this.selectProduct(data.product);
            if(data.product) {
                this.$refs.searchProduct.setSelected(data.product);
            }
        },
    }
};
</script>

<style lang="scss" scoped>
.item-form {
    padding: 24px;
    min-height: 575px;

    .highlight-section {
        background: #fafafa;
        margin: 16px 0px 26px 0px;
        border-top: 1px solid #b8b8b8;
        border-bottom:1px solid #b8b8b8;
    }

    .readonly {
        ::v-deep .v-label {
            color: var(--v-primary-base);
        }

        ::v-deep input {
            color: var(--v-primary-base);
        }

        &.highlight {
            ::v-deep .v-label {
                color: var(--v-warning-base);
            }

            ::v-deep input {
                color: var(--v-warning-base);
            }
        }
    }
}

.drawer {
    .drawer-header {
        height: 64px;
        width: 100%;
    }

    .drawer-content {
        margin-top: 60px;
        padding-left: 24px;
        padding-right: 24px;
    }
}
</style>
